import React, { forwardRef } from 'react';
import {
    StyledMenuItem,
    StyledLabel,
    ChevronIcon,
    StyledDescription,
    StyledLabelContainer
} from './SideNavMenuItem.styles';
import { IconComponent } from '@honeybook/icons';
import colors from '../../../styles/colors';
import { vars } from '../../../theme';
import HBBadgeNew, { HBBadgeNewProps } from '../../HBBadge/HBBadgeNew';
import HBNumericBadge, {
    NumericBadgeProps
} from '../../HBNumericBadge/HBNumericBadge';
import HBText from '../../HBText/HBText';
import { ISideNavTheme } from '../context/useCreateTheme';
import { ISideNavItem } from '../HBSideNav';

interface IBaseMenuItem {
    label: string;
    theme?: ISideNavTheme;
    parentLabel?: string;
    hideLabel?: boolean;
    icon?: IconComponent;
    customIcon?: () => JSX.Element;
    onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    isOpen?: boolean;
    showChevron?: boolean;
    isSelected?: boolean;
    linkProps?: Partial<Pick<HTMLAnchorElement, 'href' | 'target' | 'rel'>>;
    description?: string;
    testIdName?: string;
    persistentItemOpen?: ISideNavItem;
}

type Badge =
    | { badgeType?: 'new'; badgeProps?: HBBadgeNewProps }
    | { badgeType: 'numeric'; badgeProps: NumericBadgeProps };

export type IMenuItem = IBaseMenuItem & Badge;

const SideNavMenuItem = forwardRef(
    (
        {
            label,
            theme,
            parentLabel,
            hideLabel,
            icon: Icon,
            customIcon: CustomIcon,
            onClick,
            isOpen,
            showChevron,
            isSelected,
            linkProps = {},
            description,
            badgeProps,
            badgeType,
            persistentItemOpen,
            testIdName,
            ...props
        }: IMenuItem,
        ref: React.Ref<HTMLAnchorElement>
    ) => {
        let badge = null;
        if (badgeProps) {
            if (badgeType === 'numeric') {
                badge = <HBNumericBadge {...badgeProps} />;
            } else if (!badgeType || badgeType === 'new') {
                badge = <HBBadgeNew size="xs" type="accent" {...badgeProps} />;
            }
        }

        // StyledLabel fades out when minimized, so the collapsed count anchors to the item.
        const collapsedBadge =
            hideLabel && badgeType === 'numeric' && badgeProps ? (
                <HBNumericBadge
                    theme={badgeProps.theme}
                    number={badgeProps.number}
                    max={9}
                    hasBorder
                    borderColor={vars.colors.surface.itemInverted}
                    position={{ position: 'absolute', top: 3, left: 23 }}
                />
            ) : null;

        // TODO: add as={elementType} Remoeved for now because the button auto focuses and adds an outline
        // const elementType = linkProps?.href ? 'a' : 'button';

        return (
            <StyledMenuItem
                ref={ref}
                href="#"
                as="a"
                $theme={theme}
                $preventPointerEvents={
                    persistentItemOpen && persistentItemOpen?.label !== label
                }
                onClick={onClick}
                isSelected={isSelected}
                isOpen={isOpen}
                isMinimized={hideLabel}
                aria-current={isSelected && !showChevron ? 'page' : undefined}
                {...linkProps}
                data-testid={
                    testIdName ? `menu-item-${testIdName}` : 'menu-item'
                }
                {...props}
            >
                {collapsedBadge}
                <StyledLabelContainer>
                    {Icon && <Icon color={theme.itemTextColor} />}
                    {!Icon && CustomIcon && <CustomIcon />}
                    {label && (
                        <StyledLabel hideLabel={hideLabel}>
                            <HBText
                                type={
                                    parentLabel
                                        ? isSelected
                                            ? 'emphasis14'
                                            : 'body14'
                                        : isSelected
                                        ? 'emphasis16'
                                        : 'body16'
                                }
                            >
                                {label}
                            </HBText>
                            {badge}
                        </StyledLabel>
                    )}
                    {showChevron && !hideLabel && (
                        <ChevronIcon
                            isOpen={isOpen}
                            color={
                                isOpen ? colors.neutral800 : colors.neutral400
                            }
                        />
                    )}
                </StyledLabelContainer>
                {description && (
                    <StyledDescription type="body12" color="neutral600">
                        {description}
                    </StyledDescription>
                )}
            </StyledMenuItem>
        );
    }
);

export default SideNavMenuItem;
